import { useState } from "react";
import { Home, Briefcase } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FormError } from "@/pages/auth/AuthShell";
import { TimezoneSelect } from "./TimezoneSelect";
import { errorMessage } from "@/lib/api";

const guessTz = () => Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Los_Angeles";
const ROLES = [["homeowner", "Homeowner", "Your own home or properties", Home], ["agent", "Real estate agent", "Paperwork for your clients' properties", Briefcase]];

export const AccountForm = ({ initial, onSubmit, submitLabel, disabled = false, children }) => {
  const [form, setForm] = useState({ name: "", account_type: "", business_name: "", timezone: guessTz(), ...initial, business_name: initial?.business_name || "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: typeof v === "string" ? v : v.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.account_type) return setError("Please choose homeowner or real estate agent.");
    setBusy(true);
    try { await onSubmit({ ...form, business_name: form.account_type === "agent" ? form.business_name : null }); }
    catch (err) { setError(errorMessage(err)); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="account-form">
      <div className="space-y-2">
        <Label>I'm using EVR as a</Label>
        <div className="grid gap-3 sm:grid-cols-2">
          {ROLES.map(([v, label, help, Icon]) => (
            <button type="button" key={v} disabled={disabled} onClick={() => set("account_type")(v)} data-testid={`account-type-${v}`}
              className={`flex items-start gap-3 rounded-lg border p-4 text-left transition-colors ${form.account_type === v ? "border-gold bg-gold-light/50" : "hover:border-navy/30"}`}>
              <Icon className="mt-0.5 h-5 w-5 text-gold-dark" />
              <span><span className="block text-sm font-medium text-navy">{label}</span><span className="text-xs text-slate-500">{help}</span></span>
            </button>
          ))}
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="acct-name">{form.account_type === "agent" ? "Your name or team name" : "Workspace name"}</Label>
        <Input id="acct-name" data-testid="workspace-name-input" value={form.name} onChange={set("name")} required disabled={disabled} className="h-11"
          placeholder={form.account_type === "agent" ? "e.g. your name" : "e.g. your household name"} />
      </div>
      {form.account_type === "agent" && (
        <div className="space-y-2">
          <Label htmlFor="acct-biz">Business or brokerage name (optional)</Label>
          <Input id="acct-biz" data-testid="workspace-business-input" value={form.business_name} onChange={set("business_name")} disabled={disabled} className="h-11" />
        </div>
      )}
      <div className="space-y-2">
        <Label>Your timezone</Label>
        <TimezoneSelect value={form.timezone} onChange={set("timezone")} disabled={disabled} testId="workspace-timezone-select" />
      </div>
      {children}
      <FormError message={error} testId="workspace-form-error" />
      {!disabled && (
        <Button type="submit" disabled={busy} data-testid="workspace-submit-button" className="h-11 w-full bg-navy hover:bg-navy-2">{busy ? "Saving..." : submitLabel}</Button>
      )}
    </form>
  );
};
